import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { LexoRank } from "lexorank";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
import { isWhitespace } from "src/components/textEditor";
import { useSession } from "src/hooks";
import { v4 as newGuid } from "uuid";
import { CreateEditMetricForm, ICreateEditMetricFormState } from ".";
import { ConnectObjectType, GRAPH_COLORS } from "../../constants";
import { useCreateMetric, useGetMetricCollection, useGetTeams } from "../../http";
import { lexorank } from "../../utilities";

interface IParams {
  slug: string;
}

interface IProps {
  visible: boolean;
  onAdded: (metricGuid: string) => void;
  onCancelled: () => void;
}

const CreateMetricModal = (props: IProps) => {
  const sessionUser = useSession();
  const { slug } = useParams<keyof IParams>() as IParams;
  const { data: teams } = useGetTeams(undefined, props.visible);

  const form = useForm<ICreateEditMetricFormState>({
    defaultValues: {
      title: "",
      description: "",
      assignedTeam: null,
      reportingUser: {
        displayName: sessionUser.displayName,
        profilePhotoSmallUrl: sessionUser.profilePhotoSmallUrl,
        userId: sessionUser.userId,
        enabled: true,
      },
      datasets: [
        {
          guid: newGuid(),
          title: "Current",
          displayAsBar: false,
          color: GRAPH_COLORS[2],
          dotted: false,
          data: [],
          sortOrder: LexoRank.middle().toString(),
        },
        {
          guid: newGuid(),
          title: "Target",
          displayAsBar: false,
          color: GRAPH_COLORS[4],
          dotted: true,
          data: [],
          sortOrder: LexoRank.middle().genNext().toString(),
        },
      ],
      annotations: [],
      tags: [],
    },
  });

  const assignedTeam = form.watch("assignedTeam");
  const { data: metrics, isInitialLoading } = useGetMetricCollection(assignedTeam?.metricCollectionGuid ?? null);
  const { mutate, isLoading: isCreating } = useCreateMetric();

  useEffect(() => {
    if (props.visible) {
      form.reset({}, { keepDefaultValues: true });
    }
  }, [form, props.visible]);

  useEffect(() => {
    if (teams) {
      form.setValue("assignedTeam", teams.find((x) => x.slug === slug) ?? null);
    }
  }, [form, slug, teams, props.visible]);

  async function handleCreateClicked() {
    form.handleSubmit((formState) => {
      const newMetricGuid = newGuid();

      mutate(
        {
          newMetricGuid: newMetricGuid,
          teamSlug: slug,
          title: formState.title,
          description: formState.description,
          assignedTeamSlug: formState.assignedTeam?.slug,
          reportingUserGuid: formState.reportingUser?.userId,
          sortOrder: lexorank.getNextRank(metrics),
          datasets: formState.datasets.map((dataset) => {
            return {
              guid: dataset.guid,
              title: dataset.title,
              displayAsBar: dataset.displayAsBar,
              color: dataset.color,
              dotted: dataset.dotted,
              data: dataset.data.map((data) => {
                return {
                  rowGuid: data.rowGuid,
                  date: data.date.toString(),
                  value: data.value,
                };
              }),
              sortOrder: dataset.sortOrder,
            };
          }),
          annotations: formState.annotations
            .filter((annotation) => annotation.text !== null && !isWhitespace(annotation.text))
            .map((annotation) => {
              return {
                rowGuid: annotation.rowGuid,
                text: annotation.text!,
              };
            }),
          tags: formState.tags,
        },
        {
          onSuccess: (data) => {
            props.onAdded(data.metricGuid);
          },
        },
      );
    })();
  }

  function handleClose(_: object, reason: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancelClicked();
  }

  function handleCancelClicked() {
    props.onCancelled();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose}>
      <DialogTitle>Create {ConnectObjectType.Metric}</DialogTitle>
      <DialogContent>
        <CreateEditMetricForm form={form} />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isCreating} onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isInitialLoading || isCreating}
          endIcon={isInitialLoading || isCreating ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
          onClick={handleCreateClicked}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { CreateMetricModal };
